import { useEffect, useState } from 'react';
import {
  ArrowLeft,
  BarChart3,
  CircleDollarSign,
  LayoutDashboard,
  LineChart,
  MailCheck,
  Receipt,
  RefreshCw,
  Send,
  ShoppingBag,
  UserCheck,
  Users,
} from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useCampaignScope } from '../model/campaign-scope';
import { useCampaignReport, type CampaignReportTotals } from '../api/use-campaign-report';
import { useCampaignMetabaseEmbed } from '../api/use-campaign-metabase';
import { PageHeader } from '@/shared/components/page-header';
import { KpiCard } from '@/shared/components/kpi-card';
import { MetabaseIframePanel } from '@/shared/components/metabase-iframe-panel';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { Skeleton } from '@/shared/ui/skeleton';
import { brl, decimal, formatDate } from '@/shared/lib/format';
import { DispatchesChart, FunnelBars, PurchaseLagChart, RevenueChart } from './campaign-report-charts';
import { BuyersTable, DispatchesTable } from './campaign-report-tables';

type Period = '30' | '90' | '180';

const PERIODS = [
  { value: '30' as const, label: '30 dias' },
  { value: '90' as const, label: '90 dias' },
  { value: '180' as const, label: '180 dias' },
];

type View = 'report' | 'metabase';

/** O painel do Metabase que ficava aqui antes continua a um clique (`?aba=metabase`). */
const VIEWS = [
  { value: 'report' as const, label: 'Relatório', icon: BarChart3 },
  { value: 'metabase' as const, label: 'Metabase', icon: LayoutDashboard },
];

type Section = 'charts' | 'tables';

/** Gráficos e tabelas em abas (`?secao=tabelas`), para a página não ficar comprida. */
const SECTIONS = [
  { value: 'charts' as const, label: 'Gráficos', icon: LineChart },
  { value: 'tables' as const, label: 'Clientes e disparos', icon: Users },
];

function ChartCard({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function ordersHint(totals?: CampaignReportTotals): string {
  const base = 'Compras feitas por quem recebeu, dentro da janela.';
  if (!totals?.reversedOrders) return base;
  const reversed = totals.reversedOrders === 1 ? 'estornada ficou' : 'estornadas ficaram';
  return `${base} ${decimal(totals.reversedOrders)} ${reversed} de fora.`;
}

function revenueHint(totals?: CampaignReportTotals): string {
  if (totals && totals.ordersWithValue < totals.orders) {
    return `Valor do cupom em ${decimal(totals.ordersWithValue)} de ${decimal(totals.orders)} pedidos; os outros vieram do PDV sem valor.`;
  }
  return 'Soma do valor do cupom das compras atribuídas à campanha.';
}

export function CampaignResultsPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { idPassbook, listPath } = useCampaignScope();
  const [period, setPeriod] = useState<Period>('90');
  const [params, setParams] = useSearchParams();
  const view: View = params.get('aba') === 'metabase' ? 'metabase' : 'report';
  const section: Section = params.get('secao') === 'tabelas' ? 'tables' : 'charts';
  // Aba e seção ficam na URL: voltar do lead aberto pela tabela cai na mesma seção.
  const setParam = (key: string, value: string | null) => {
    const copy = new URLSearchParams(params);
    if (value) copy.set(key, value);
    else copy.delete(key);
    setParams(copy, { replace: true });
  };
  const setView = (next: View) => setParam('aba', next === 'metabase' ? 'metabase' : null);
  const setSection = (next: Section) => setParam('secao', next === 'tables' ? 'tabelas' : null);
  const { data, isLoading, isFetching, error, refresh } = useCampaignReport(
    idPassbook,
    campaignId,
    Number(period),
    view === 'report',
  );
  const metabase = useCampaignMetabaseEmbed(idPassbook, campaignId, view === 'metabase');

  const totals = data?.totals;
  const loading = isLoading || !data;
  const periodLabel =
    data && data.totals.dispatches > 0
      ? `De ${formatDate(data.period.from)} a ${formatDate(data.period.to)}, até o último disparo.`
      : undefined;
  const lagLabel = data
    ? `Dias entre o disparo e a compra. Conta até ${data.windowDays} ${data.windowDays === 1 ? 'dia' : 'dias'}.`
    : 'Dias entre o disparo e a compra.';

  // A lista de campanhas costuma deixar a página rolada; o resultado abre no topo.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [campaignId]);

  return (
    <>
      <PageHeader
        title="Resultados da campanha"
        hideMetaOnMobile
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {view === 'report' ? (
              <>
                <SegmentedControl value={period} onChange={setPeriod} options={PERIODS} size="sm" />
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => void refresh()}
                  disabled={isFetching}
                  aria-label="Atualizar"
                  title={
                    data
                      ? `Calculado às ${new Date(data.generatedAt).toLocaleTimeString('pt-BR', { timeStyle: 'short' })}. Clique para refazer a conta.`
                      : 'Atualizar'
                  }
                >
                  <RefreshCw className={isFetching ? 'size-4 animate-spin' : 'size-4'} />
                </Button>
              </>
            ) : null}
            <Button variant="secondary" asChild>
              <Link to={listPath}>
                <ArrowLeft className="size-4" /> Voltar
              </Link>
            </Button>
          </div>
        }
      />

      <SegmentedControl value={view} onChange={setView} options={VIEWS} size="sm" fill className="mb-4" />

      {view === 'metabase' ? (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            É o painel que ficava nesta tela antes do relatório. Ele lê uma cópia dos dados atualizada de hora em hora e
            conta compras e receita com regras próprias, por isso os números podem não bater com os do relatório.
          </p>
          <MetabaseIframePanel
            src={metabase.data?.iframeUrl}
            title="Resultados da campanha no Metabase"
            isLoading={metabase.isLoading}
            isError={metabase.isError}
            errorMessage={metabase.error instanceof Error ? metabase.error.message : undefined}
            icon={LayoutDashboard}
          />
        </div>
      ) : error && !data ? (
        <div className="glass flex h-48 items-center justify-center rounded-2xl p-6 text-sm text-destructive">
          {(error as Error).message || 'Resultados indisponíveis'}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
            <KpiCard
              index={0}
              label="Disparos"
              icon={Send}
              loading={loading}
              value={decimal(totals?.dispatches)}
              hint="Quantas vezes a campanha rodou no período."
            />
            <KpiCard
              index={1}
              label="Receberam"
              icon={MailCheck}
              loading={loading}
              value={decimal(totals?.recipients)}
              hint={`${decimal(totals?.reachRate, 1)}% das ${decimal(totals?.audience)} pessoas do público. Cada pessoa conta uma vez, mesmo com app e carteira.`}
            />
            <KpiCard
              index={2}
              label="Compraram"
              icon={UserCheck}
              loading={loading}
              value={decimal(totals?.buyers)}
              hint={`${decimal(totals?.conversionRate, 1)}% das ${decimal(totals?.recipients)} pessoas que receberam.`}
            />
            <KpiCard
              index={3}
              label="Pedidos"
              icon={ShoppingBag}
              loading={loading}
              value={decimal(totals?.orders)}
              hint={ordersHint(totals)}
            />
            <KpiCard
              index={4}
              label="Receita"
              icon={CircleDollarSign}
              loading={loading}
              value={brl(totals?.revenue)}
              hint={revenueHint(totals)}
            />
            <KpiCard
              index={5}
              label="Ticket médio"
              icon={Receipt}
              loading={loading}
              value={brl(totals?.avgTicket)}
              hint="Receita ÷ pedidos com valor no cupom."
            />
          </div>

          <SegmentedControl value={section} onChange={setSection} options={SECTIONS} fill />

          {section === 'charts' ? (
            <div className="grid gap-4 lg:grid-cols-2">
              <ChartCard title="Do envio à compra" description="Quantas pessoas avançam em cada etapa.">
                {data ? <FunnelBars totals={data.totals} /> : <Skeleton className="h-60 w-full rounded-xl" />}
              </ChartCard>
              <ChartCard title="Quando elas compram" description={lagLabel}>
                {data ? <PurchaseLagChart lag={data.purchaseLag} /> : <Skeleton className="h-60 w-full rounded-xl" />}
              </ChartCard>
              <ChartCard title="Entregas por dia" description={periodLabel}>
                {data ? <DispatchesChart daily={data.daily} /> : <Skeleton className="h-60 w-full rounded-xl" />}
              </ChartCard>
              <ChartCard title="Receita atribuída por dia">
                {data ? <RevenueChart daily={data.daily} /> : <Skeleton className="h-60 w-full rounded-xl" />}
              </ChartCard>
            </div>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Clientes que compraram</CardTitle>
                  <CardDescription>Clique no nome para abrir o cliente em Leads.</CardDescription>
                </CardHeader>
                <CardContent>
                  {data ? (
                    <BuyersTable buyers={data.buyers} truncated={data.buyersTruncated} idPassbook={idPassbook} />
                  ) : (
                    <Skeleton className="h-64 w-full rounded-xl" />
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Histórico de disparos</CardTitle>
                </CardHeader>
                <CardContent className="px-0">
                  {data ? <DispatchesTable dispatches={data.dispatches} /> : <Skeleton className="mx-5 h-40 rounded-xl" />}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </>
  );
}
